import { withTranslation, WithTranslation } from 'react-i18next';
import React, { ChangeEvent, useEffect, useState } from 'react';
import getManagingSoftwares from '../../../../helpers/managingSoftwares';
import i18n from 'i18next';
import { handleInputWithLabelOnBlur, handleInputWithLabelOnFocus } from '../../registration';
import InputField from '../../../form/inputField';
import DropdownField from '../../../form/dropdownField';
import PcaField from '../../../form/pcaField';
import PopoverContainer from '../../../popover/popover';
import Notification from '../../../notification/notification';
import { ISoftwareModel } from '../../../../helpers/models/softwareModel';
import { IAccountModel } from '../../../../helpers/models/accountModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { ProfessionalStep1FormResultKeys } from './professionalStep1FormModel';
import {
    FORM_PHONE_EXT_MAX_LENGTH,
    FORM_PHONE_MAX_LENGTH,
    validatePhoneNumberFormat,
} from '../../utils/formValidation';
import { LocationModel } from '../../../../helpers/models/locationModel';

export interface IProfessionalStep1PracticeLocationProps extends WithTranslation {
    account: IAccountModel;
    handleOnDeleteClick?: any;
    index: number;
    readonly?: boolean;

    /*
     * Locations overwrite has been added because DOM changes were not correctly handled by REACT states
     * For some reason, locations element wasn't correctly update according to his default value
     * */
    locationsOverwrite?: LocationModel[];
}

const ProfessionalStep1PracticeLocation = (props: IProfessionalStep1PracticeLocationProps) => {
    const { t, handleOnDeleteClick, index, readonly, account, locationsOverwrite } = props;
    const [enabledSoftwares, setEnabledSoftwares] = useState<JSX.Element[]>([]);
    const [disabledSoftwares, setDisabledSoftwares] = useState<JSX.Element[]>([]);
    const [otherSoftwares, setOtherSoftwares] = useState<JSX.Element[]>([]);

    const ENABLED_TYPE = 'enabled';
    const DISABLED_TYPE = 'notenabled';

    const FIELD_COUNT = 7;
    const TAB_COUNT = 16;

    useEffect(() => {
        async function getData() {
            const softwaresList = await getManagingSoftwares(i18n.language);
            if (softwaresList) {
                setEnabledSoftwares(
                    softwaresList!
                        .filter((item: ISoftwareModel) => {
                            return item.type === ENABLED_TYPE;
                        })
                        .map((software: ISoftwareModel, index) => {
                            return (
                                <option
                                    key={`enabled_software_${index}`}
                                    value={software.id}
                                    data-datatype={software.type}
                                >
                                    {software.name}
                                </option>
                            );
                        })
                );
                setDisabledSoftwares(
                    softwaresList!
                        .filter((item: ISoftwareModel) => {
                            return item.type === DISABLED_TYPE;
                        })
                        .map((software: ISoftwareModel, index) => {
                            return (
                                <option
                                    key={`disabled_software_${index}`}
                                    value={software.id}
                                    data-datatype={software.type}
                                >
                                    {software.name}
                                </option>
                            );
                        })
                );

                setOtherSoftwares(
                    softwaresList!
                        .filter((item: ISoftwareModel) => {
                            return item.type !== DISABLED_TYPE && item.type !== ENABLED_TYPE;
                        })
                        .map((software: ISoftwareModel, index) => {
                            return (
                                <option key={`software_option_${index}`} value={software.id}>
                                    {software.name}
                                </option>
                            );
                        })
                );
            }
        }

        getData();
    }, [i18n.language]);

    const softwares = [
        <React.Fragment key="software">
            {enabledSoftwares.length ? (
                <optgroup
                    key="enabled"
                    label={t('form_professional_step_1_field_managing_software_activated')}
                >
                    {enabledSoftwares}
                </optgroup>
            ) : null}
            {disabledSoftwares.length ? (
                <optgroup
                    key="notenabled"
                    label={t('form_professional_step_1_field_managing_software_disabled')}
                >
                    {disabledSoftwares}
                </optgroup>
            ) : null}
            {otherSoftwares}
        </React.Fragment>,
    ];

    const handleSoftwareChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const isEnabled = e.target[e.target.selectedIndex].getAttribute('data-datatype') === 'enabled';
        document.getElementById(`form_authorization_eligible_label_${index}`)!.style.display =
            isEnabled ? 'block' : 'none';

        if (!isEnabled) {
            (
                document.getElementById(
                    `${ProfessionalStep1FormResultKeys.COMMUNICATION}_${index}`
                )! as HTMLInputElement
            ).checked = false;
        }

        const softwareOtherElement = document.getElementById(
            `form_professional_step_1_software_other_${index}`
        )! as HTMLInputElement;
        softwareOtherElement.style.display = e.target.value === '1' ? 'unset' : 'none';
        if (e.target.value === '1') {
            softwareOtherElement.setAttribute('required', '');
        } else {
            softwareOtherElement.removeAttribute('required');
            (
                document.getElementById(
                    ProfessionalStep1FormResultKeys.MANAGING_SOFTWARE_OTHER + `_${index}`
                )! as HTMLInputElement
            ).value = '';
        }
    };

    useEffect(() => {
        if (account.locations.length >= index && account.locations.at(index)) {
            const softwareSelectElement = document.getElementById(
                `${ProfessionalStep1FormResultKeys.MANAGING_SOFTWARE}_${index}`
            )! as HTMLSelectElement;
            if (softwareSelectElement?.options.length > 1) {
                document.getElementById(
                    `form_authorization_eligible_label_${index}`
                )!.style.display = softwareSelectElement
                    .options[softwareSelectElement.options.selectedIndex].getAttribute('data-datatype') === 'enabled'
                    ? 'block'
                    : 'none';
                document.getElementById(
                    `form_professional_step_1_software_other_${index}`
                )!.style.display =
                    softwareSelectElement.options[softwareSelectElement.options.selectedIndex]
                        ?.value === '1'
                        ? 'unset'
                        : 'none';
            }
        }
    }, [softwares]);

    const locationToDisplay =
        locationsOverwrite && locationsOverwrite.length > index
            ? locationsOverwrite[index]
            : account.locations[index];
    const agreeManagementSoftware = locationToDisplay?.agreeManagementSoftware || false;
    return (
        <>
            <div className="registration_form_header--section">
                {index === 0 ? (
                    <h1 className="header-3 header-3--purple registration_form_header">
                        {t('form_professional_step_1_profile_title').addSpace() + (index + 1)}
                    </h1>
                ) : (
                    <div className="header-3 header-3--purple registration_form_header">
                        {t('form_professional_step_1_profile_title').addSpace() + (index + 1)}
                    </div>
                )}

                {!readonly && index > 0 && (
                    <button
                        data-id={index}
                        className="header-3 header-3--purple registration_form--delete-button"
                        role="button"
                        aria-label={
                            t('form_professional_step_1_profile_delete_button_aria_label', {
                                ns: 'accessibility',
                            }) +
                            ' ' +
                            t('form_professional_step_1_profile_title') +
                            ' ' +
                            (index + 1)
                        }
                        id={`form_professional_step_1_delete_${index}`}
                        onClick={(e) => handleOnDeleteClick(e)}
                        tabIndex={16 + index * TAB_COUNT}
                    >
                        <span className="visuallyhidden">
                            {t('form_professional_step_1_profile_delete_button_aria_label', {
                                ns: 'accessibility',
                            })}
                        </span>
                    </button>
                )}
            </div>
            {!readonly && index <= 0 && (
                <Notification
                    error={false}
                    label={t('popover_aria_label', { ns: 'accessibility' })}
                    messages={[
                        { message: t('form_professional_step_1_profile_notification'), weight: 0 },
                    ]}
                />
            )}
            <div className="registration-form-container">
                <div className="registration-section">
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={ProfessionalStep1FormResultKeys.COMPANY_NAME + `_${index}`}
                        fieldTitle="form_professional_step_1_field_company_name"
                        fieldDisabled={readonly}
                        fieldRequired
                        type="text"
                        defaultValue={locationToDisplay?.name ?? ''}
                        fieldWeight={index * FIELD_COUNT}
                        tabIndex={readonly ? 0 : 3 + index * TAB_COUNT}
                        fieldHiddenLabel="form_professional_step_1_profile_title"
                    />
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-field registration-contact-info-phone-field registration-column-item"
                            fieldName={ProfessionalStep1FormResultKeys.PHONE + `_${index}`}
                            fieldTitle="form_professional_step_1_field_phone"
                            fieldDisabled={readonly}
                            fieldRequired
                            type="text"
                            placeholder="999-999-9999"
                            defaultValue={locationToDisplay?.phone ?? ''}
                            fieldWeight={1 + index * FIELD_COUNT}
                            maxLength={FORM_PHONE_MAX_LENGTH}
                            handleOnChange={validatePhoneNumberFormat}
                            fieldHiddenLabel="form_professional_step_1_profile_title"
                            tabIndex={readonly ? 0 : 4 + index * TAB_COUNT}
                        />
                        <InputField
                            fieldContainerClassName="registration-field registration-column-item registration-contact-info-ext-field"
                            fieldName={ProfessionalStep1FormResultKeys.PHONE_EXT + `_${index}`}
                            fieldTitle="form_professional_step_1_field_phone_ext"
                            fieldOptionalText="form_professional_step_1_field_phone_ext_optional"
                            fieldDisabled={readonly}
                            inputMode="numeric"
                            defaultValue={locationToDisplay?.ext ?? ''}
                            fieldWeight={2 + index * FIELD_COUNT}
                            maxLength={FORM_PHONE_EXT_MAX_LENGTH}
                            fieldHiddenLabel="form_professional_step_1_profile_title"
                            handleOnChange={validatePhoneNumberFormat}
                            tabIndex={readonly ? 0 : 5 + index * TAB_COUNT}
                        />
                    </div>
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={ProfessionalStep1FormResultKeys.COMMERCIAL_BANNER + `_${index}`}
                        fieldTitle="form_professional_step_1_field_commercial_banner"
                        fieldOptionalText="form_professional_step_1_field_commercial_banner_optional"
                        fieldDisabled={readonly}
                        fieldDisabledPopover={readonly}
                        fieldHiddenLabel="form_professional_step_1_profile_title"
                        fieldTooltip={
                            <PopoverContainer
                                icon
                                className={'field-tooltip-icon'}
                                tabIndex={readonly ? -1 : 6 + index * TAB_COUNT}
                                prefix={t('form_professional_step_1_field_commercial_banner')}
                            >
                                <p className="body-medium">
                                    {t('form_professional_step_1_field_commercial_banner_popover')}
                                </p>
                            </PopoverContainer>
                        }
                        type="text"
                        defaultValue={locationToDisplay?.retailBanner ?? ''}
                        fieldWeight={2 + index * FIELD_COUNT}
                        tabIndex={readonly ? 0 : 7 + index * TAB_COUNT}
                    />
                </div>
                <div className="registration-section">
                    {!readonly && (
                        <PcaField
                            fieldContainerClassName="registration-field"
                            fieldName={ProfessionalStep1FormResultKeys.PCA + `_${index}`}
                            fieldTitle="form_professional_step_1_field_pca"
                            fieldId={`form_professional_step_1_field_pca_${index}`}
                            fieldPca="Line1"
                            fieldHiddenLabel="form_professional_step_1_profile_title"
                            fieldDisabled={readonly}
                            fieldAddress={ProfessionalStep1FormResultKeys.ADDRESS + `_${index}`}
                            fieldCity={ProfessionalStep1FormResultKeys.CITY + `_${index}`}
                            fieldPostalCode={ProfessionalStep1FormResultKeys.POSTAL_CODE + `_${index}`}
                            defaultValue={locationToDisplay?.address ?? ''}
                            fieldWeight={5 + index * FIELD_COUNT}
                            fieldAccountProvinceCode={(account.province as IProvinceModel).pca}
                            tabIndex={11 + index * TAB_COUNT}
                            fieldDisabledPopover={readonly}
                            fieldTooltip={
                                <PopoverContainer
                                    icon
                                    className={'field-tooltip-icon'}
                                    prefix={t('form_professional_step_1_field_pca')}
                                >
                                    <p className="body-medium">
                                        {t('form_professional_step_1_field_pca_popover')}
                                    </p>
                                </PopoverContainer>
                            }
                        />
                    )}
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={`${ProfessionalStep1FormResultKeys.ADDRESS}_${index}`}
                        fieldTitle="form_professional_step_1_field_address"
                        fieldDisabled
                        type="text"
                        defaultValue={locationToDisplay?.address ?? ''}
                        fieldWeight={6}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />

                    <InputField
                        fieldContainerClassName="registration-field registration-column-item"
                        fieldName={ProfessionalStep1FormResultKeys.CITY + `_${index}`}
                        fieldTitle="form_professional_step_1_field_city"
                        fieldDisabled
                        type="text"
                        fieldHiddenLabel="form_professional_step_1_profile_title"
                        defaultValue={locationToDisplay?.city ?? ''}
                        fieldWeight={6 + index * FIELD_COUNT}
                        tabIndex={0}
                    />
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-field registration-column-item"
                            fieldName={ProfessionalStep1FormResultKeys.PROVINCE + `_${index}`}
                            fieldTitle="form_professional_step_1_field_province"
                            fieldDisabled
                            fieldHiddenLabel="form_professional_step_1_profile_title"
                            type="text"
                            defaultValue={(account.province as IProvinceModel).name}
                            fieldWeight={0}
                            tabIndex={readonly ? 0 : 14 + index * TAB_COUNT}
                            fieldTooltip={!readonly
                                ? (
                                    <PopoverContainer
                                        icon
                                        className={'field-tooltip-icon'}
                                        triggerContent={t('form_professional_step_1_field_province_inline')}
                                        tabIndex={15 + index * TAB_COUNT}
                                        prefix={t('form_professional_step_1_field_province')}
                                    >
                                        <>
                                            <p className="body-small">
                                                <strong>
                                                    {t('form_professional_step_1_field_province_inline')}
                                                </strong>
                                            </p>
                                            <p className="body-medium">
                                                {t(
                                                    'form_professional_step_1_field_province_popover_register_locations'
                                                )}
                                            </p>
                                            <p className="body-medium">
                                                {t('form_professional_step_1_field_province_popover_tel')}
                                                <br/>
                                                <a
                                                    className="standalone-link"
                                                    tabIndex={-1}
                                                    href={`mailto:${t(
                                                        'form_professional_step_1_field_province_popover_email'
                                                    )}`}
                                                >
                                                    {t(
                                                        'form_professional_step_1_field_province_popover_email'
                                                    )}
                                                </a>
                                            </p>
                                        </>
                                    </PopoverContainer>
                                )
                                : undefined
                            }
                        />
                        <InputField
                            fieldContainerClassName="registration-field"
                            fieldName={ProfessionalStep1FormResultKeys.POSTAL_CODE + `_${index}`}
                            fieldTitle="form_professional_step_1_field_postal_code"
                            fieldDisabled
                            fieldHiddenLabel="form_professional_step_1_profile_title"
                            type="text"
                            maxLength={7}
                            defaultValue={locationToDisplay?.zip ?? ''}
                            fieldWeight={7 + index * FIELD_COUNT}
                            tabIndex={0}
                        />
                    </div>
                </div>
            </div>
            <div className="registration-column-container">
                <DropdownField
                    fieldContainerClassName="registration-field registration-column-item registration-field--no-margin"
                    fieldName={ProfessionalStep1FormResultKeys.MANAGING_SOFTWARE + `_${index}`}
                    fieldTitle="form_professional_step_1_field_managing_software"
                    fieldDisabled={readonly}
                    fieldDisabledPopover={readonly}
                    fieldHiddenLabel="form_professional_step_1_profile_title"
                    fieldWeight={4 + index * FIELD_COUNT}
                    tabIndex={readonly ? 0 : 9 + index * TAB_COUNT}
                    fieldTooltip={
                        <PopoverContainer
                            tabIndex={readonly ? -1 : 8 + index * TAB_COUNT}
                            icon
                            className={'field-tooltip-icon'}
                            prefix={t('form_professional_step_1_field_managing_software')}
                        >
                            <>
                                <p className="body-medium">
                                    {t(
                                        'form_professional_step_1_field_managing_software_popover_authorisation'
                                    )}
                                </p>
                                <ul>
                                    <li className="body-medium">
                                        {t(
                                            'form_professional_step_1_field_managing_software_popover_info_share'
                                        )}
                                    </li>
                                    <li className="body-medium">
                                        {t(
                                            'form_professional_step_1_field_managing_software_popover_facilitate_service'
                                        )}
                                    </li>
                                </ul>
                                <p className="body-medium">
                                    <i>
                                        {t(
                                            'form_professional_step_1_field_managing_software_popover_subscription_fee'
                                        )}
                                    </i>
                                </p>
                            </>
                        </PopoverContainer>
                    }
                    fieldDefaultValue="form_professional_step_1_field_managing_software_default"
                    fieldOptions={softwares}
                    fieldRequired
                    value={(locationToDisplay?.managementSoftware as ISoftwareModel)?.id ?? ''}
                    handleOnChange={handleSoftwareChange}
                />
                <InputField
                    fieldContainerClassName="registration-field registration-field--no-margin-lg registration-column-item"
                    fieldName={
                        ProfessionalStep1FormResultKeys.MANAGING_SOFTWARE_OTHER + `_${index}`
                    }
                    fieldTitle="form_professional_step_1_field_managing_software_other"
                    fieldId={`form_professional_step_1_software_other_${index}`}
                    fieldWeight={5 + index * FIELD_COUNT}
                    fieldHiddenLabel="form_professional_step_1_profile_title"
                    fieldDisabled={readonly}
                    fieldStyle={{ display: 'none' }}
                    defaultValue={locationToDisplay?.managementSoftwareOther ?? ''}
                    type="text"
                    tabIndex={readonly ? 0 : 10 + index * TAB_COUNT}
                />
            </div>
            <label
                id={`form_authorization_eligible_label_${index}`}
                style={{ display: 'none' }}
                htmlFor={ProfessionalStep1FormResultKeys.COMMUNICATION + `_${index}`}
                className={'checkbox form_authorization_eligible_label '.concat(
                    readonly ? 'field-label--disabled' : ''
                )}
            >
                <div>{t('form_professional_step_1_field_communication')}</div>
                <input
                    className="checkbox-input field-value"
                    type="checkbox"
                    id={ProfessionalStep1FormResultKeys.COMMUNICATION + `_${index}`}
                    name={ProfessionalStep1FormResultKeys.COMMUNICATION + `_${index}`}
                    onBlur={handleInputWithLabelOnBlur}
                    onFocus={handleInputWithLabelOnFocus}
                    disabled={readonly}
                    defaultChecked={agreeManagementSoftware}
                    tabIndex={readonly ? 0 : 10 + index * TAB_COUNT}
                />
                <span className="checkmark checkmark--disabled"></span>
            </label>
        </>
    );
};

export default withTranslation()(ProfessionalStep1PracticeLocation);
